import { AfterViewChecked, Component, ElementRef, EventEmitter, HostListener, inject, OnDestroy, OnInit, Output, Self, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { filter, Subject, takeUntil } from 'rxjs';
import { DOC_ORIENTATION, NgxImageCompressService } from 'ngx-image-compress';

import { StoryService } from '../story.service';
import { LayoutService } from '../../layout.service';

import { PhotoObject } from './story-photos';

import { Story } from '../../shared/interfaces/story';
import { StoriesService } from '../../stories.service';
import { DeletePhotoDialogComponent } from '../../dialog/delete-photo-dialog/delete-photo-dialog.component';
import { Dialog } from '@angular/cdk/dialog';

@Component({
  selector: 'app-images-holder',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './images-holder.component.html',
  styleUrl: './images-holder.component.scss'
})
export class ImagesHolderComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('fileUpload') fileUpload!: ElementRef
  @ViewChild('saveDialog') saveDialog!: ElementRef
  @ViewChild('holder') holder!: HTMLDivElement
  @HostListener('window:resize', ['$event'])
  @Output() currentImageDisplayed = new EventEmitter<{image: PhotoObject, index: number}>()
  @Output() removeImage = new EventEmitter<boolean>()
  @Output() openDialog = new EventEmitter<{type: string, imageToSave?: boolean}>()

  public getScreenWidth!: number;
  public getScreenHeight!: number;
  
  mobileScreenRatio!: number
  storyImages: PhotoObject[] = []
  storyWidth = 0
  private readonly _destroyed = new Subject<void>()
  isDesktop = this.layoutService.isDesktop
  storyToEdit!: Story | null
  resizeImage = false
  dialog = inject(Dialog)
  imageDeletionPermitted = true

  get height(): number {
    return this.element.nativeElement.offsetHeight;
  }
  get width(): number {
    return this.element.nativeElement.offsetWidth;
  }

  constructor(
    private layoutService: LayoutService,
    private storyService: StoryService,
    private imageCompress: NgxImageCompressService,
    private storiesService: StoriesService,
    @Self() private element: ElementRef
  ){}

  ngOnInit(){
    this.storyImages = []

    this.storyService.storyImages.pipe(takeUntil(this._destroyed)).subscribe(images => {
      this.storyImages = images
     
      if(this.storyImages.length !== 5) {
        for(let i = 0; i < 4; i++){
          if(i === 0){
            this.storyImages.push({
                src: null,
                thumbnailSrc: null,
                disabled: false,
                saved: false,
                edited: false
            })
          } else {
            this.storyImages.push({
              src: null,
              thumbnailSrc: null,
              disabled: true,
              saved: false,
              edited: false
            })
          }
        }
      }

      const imagesWithSource = images.filter(s => s.src)
      if(imagesWithSource.length <= 1) this.imageDeletionPermitted = false 

      // add thumbnail images
      imagesWithSource.forEach((i, index) => {
        if(!i.src) return
        this.createThumbnail(i.src, index)
      })
    })

    // opens the file input when user chooses to add new photo from the publish dialog
    this.storyService.openFileInput.pipe(takeUntil(this._destroyed)).subscribe(res => {
      if(!res) return
      this.fileUpload.nativeElement.click()
    })
  }

  ngAfterViewChecked(){
    if(!this.isDesktop()){
      const screenWidth = window.screen.width
      const submitButtonWidth = 64
      const gapWidth = 4
      const outlineWidth = 3 * 2
      this.storyWidth = (screenWidth - (submitButtonWidth + 16 + gapWidth)) / 5 - (gapWidth + outlineWidth)
    } else {
      const popupWidth = 600
      const submitButtonWidth = 96
      const gapWidth = 12
      const outlineWidth = 3 * 2
      this.storyWidth = (popupWidth - (submitButtonWidth + 48 + gapWidth)) / 5 - (gapWidth + outlineWidth)
    } 
  }

  getStyles(){
    return {
      width: `${this.storyWidth}px`,
      minWidth: `${this.storyWidth}px`,
      height: `${this.storyWidth * 1.4}px`
    }
  }

  getImageStyle(){
    const ratio = this.storyService.ratio
    
    if(ratio > 1.4){
      return {
        width: 'auto',
        height: '100%'
      }
    } else {
      return {
        height: 'auto',
        width: '100%'
      }
    }
  }

  onSubmit(){
    const imagesToSave = this.storyImages.filter(i => !!i.src && !i.saved)
    if(imagesToSave.length > 0) {
      this.openDialog.emit({type: "publish", imageToSave: true})
    } else {
      this.openDialog.emit({type: 'publish'})
    }
  }

  displayImage(image: PhotoObject, index: number){
    this.currentImageDisplayed.emit({image, index})
  }

  //if the previous image is saved, open the file input
  openFileInput(i: number){

    // if imageToSave, this.openDialog.emit({type: 'save', imageToSave: true})
    // if !imageToSave, this.fileUpload.nativeElement.click()

    if(i === 0 || this.storyImages[i-1].saved){
      //this.openDialog.emit({type: 'save', imageToSave: true})
      this.fileUpload.nativeElement.click()
    } else if(!this.storyImages[i-1].saved){
      this.openDialog.emit({type: 'save', imageToSave: true})
    }// } else {
    //   this.openDialog.emit({type: 'save', imageToSave: false})
    // }
  }

  //adds image to service and displays it
  addImage(target: EventTarget | null, index: number) {
    console.log('addImage')
    const fileInput = target as HTMLInputElement
    if(!fileInput) return
    if(!fileInput.files) return

    const imageFile = fileInput.files[0]
    const reader = new FileReader() 

    // it is impossible to have more than 5 images per story
    const completedImages = this.storyImages.filter(i => i.src && i.src !== '')
    if(completedImages.length > 4) {
      console.error('You cannot add more than 5 images to a story.')/* TODO: clé */
      return
    }

    reader.onload = (e: ProgressEvent<FileReader>) => {     
      const thumbnail = new Image()
      this.compressFile(e.target?.result as string, index)

      // create a thumbnail version of the image for the images holder
      this.imageCompress.compressFile(e.target?.result as string, DOC_ORIENTATION.Default, 100, 100, 100, 100).then((r)=>{
        thumbnail.src = r

        this.storyImages[index].thumbnailSrc = thumbnail.src
        if(index < this.storyImages.length - 1) this.storyImages[index+1].disabled = false
        this.storyService.storyImages.next(this.storyImages)
        this.displayImage(this.storyImages[index], index)
        this.imageDeletionPermitted = true

        if(!this.storyImages[index-1].saved){
          this.openDialog.emit({type: 'save', imageToSave: true})
        }
      })
    }
    
    reader.onloadend = () => {}
    reader.readAsDataURL(imageFile)
  }

  compressFile(image: string, index: number) {
    if(!this.resizeImage){
      const img = new Image()
        img.onload = () => {
        // limit size accepted by the API (786px X 1024px)
        if((img.width > 1024 || img.height > 1024)){
          this.imageCompress.compressFile(image, DOC_ORIENTATION.Default, 100, 100, 1024, 1024).then((r)=>{
            img.src = r
          })
        }

        this.storyImages[index].src = img.src
        this.storyService.currentPhoto.next({src: img.src})
      }

      img.src = image
    }
  }

  createThumbnail(image: string, index: number) {
    if(!this.resizeImage){
      const img = new Image()
        img.onload = () => {
        // limit size accepted by the API (786px X 1024px)
        if((img.width > 1024 || img.height > 1024)){
          this.imageCompress.compressFile(image, DOC_ORIENTATION.Default, 100, 100, 100, 100).then((r)=>{
            img.src = r
          })
        }

        this.storyImages[index].thumbnailSrc = img.src
      }

      img.src = image
    }
  }

  openDeletePhotoDialog(index: number) {
    console.log('openDeletePhotoDialog')
    this.dialog.open(DeletePhotoDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res),
    ).subscribe(() => {
      this.removeImageFromStory(index)
    })
  }

  //removes image from the story and displays the next image if there is one, setting the current image to null
  removeImageFromStory(index: number){
    const id = this.storyImages[index].id

    if(id){
      this.storiesService.deletePhoto(id).subscribe(() => {
        this.updateStoryImages(index)
      })
    } else {
      this.updateStoryImages(index)
    }
  }

  updateStoryImages(index: number){
    let i = index
    const max = 4 // max index of the storyImages array

    while(i < max){
      if(this.storyImages[i+1]?.src){ 
        this.storyImages[i].src = this.storyImages[i+1].src 
        this.storyImages[i].thumbnailSrc = this.storyImages[i+1].thumbnailSrc 
      } else {
        this.storyImages[i+1].disabled = true
        this.storyImages[i].src = null
        this.storyImages[i].thumbnailSrc = null
        this.storyImages[i].saved = false
        this.storyService.storyImages.next(this.storyImages)
        this.removeImage.next(true)
        this.displayImage(this.storyImages[i-1], i-1)
        break
      }

      i++  
    }

    if(i === max) {
      this.storyImages[i].src = null
      this.storyImages[i].thumbnailSrc = null
      this.storyImages[i].saved = false
      this.storyImages[i].disabled = false
      this.storyService.storyImages.next(this.storyImages)
      this.displayImage(this.storyImages[i-1], i-1)
      this.removeImage.next(true)
    }
  }

  ngOnDestroy(){
    this._destroyed.next()
    this._destroyed.complete()
  }
}

